import { ClientDirectoryApi } from '../../api/generated/apis/ClientDirectoryApi';
import { FilterOptionsApi } from '../../api/generated/apis/FilterOptionsApi';
import { UsageApi, type GetDashboardUsageGroupByEnum } from '../../api/generated/apis/UsageApi';
import type { ClientDirectoryEntry } from '../../api/generated/models/ClientDirectoryEntry';
import type { FilterOptionsResponse } from '../../api/generated/models/FilterOptionsResponse';
import type { GroupedUsageRow } from '../../api/generated/models/GroupedUsageRow';
import { Configuration, type FetchAPI } from '../../api/generated/runtime';
import { AccessTokenProvider } from '../realtime/access-token.provider';
import type { OpenSetDimension, ResolvedDateRange } from './filter-state.model';

/**
 * A named adapter over the **already-committed** generated client, following
 * the `GeneratedRealtimeGrantClient` pattern: no direct `fetch`/`HttpClient`
 * anywhere in application code (both are lint-banned), and every call goes
 * through a published operation.
 *
 * Two sources, deliberately different in kind:
 *
 * - app/model/provider reuse AITH-28's `GET /dashboard/usage?groupBy=…`, whose
 *   `GroupedUsageRow.dimensionValue` already enumerates the observed values for
 *   a window (R-001). Zero contract work was needed for these.
 * - the client directory is AITH-44's own `GET /api/v1/clients` (R-004) —
 *   `dim_client` was reachable only as a point lookup before it.
 * - GH-164: a staff caller with NO client selected reads all three open-set
 *   lists at once from `GET /api/v1/dashboard/filter-options` — the slot R-003
 *   left empty. Only where a selector can use it (decisions D-12).
 */

/** Every failure — 4xx, 5xx, network, missing token, malformed body — is one outcome. */
export type FilterOptionsFetch<T> = { readonly ok: true; readonly value: T } | { readonly ok: false };

export abstract class FilterOptionsAdapter {
  /**
   * Grouped-usage rows for one open-set dimension over the probe window.
   *
   * @param clientCode The staff drill-down target, or `null` to omit the
   * parameter entirely. `/dashboard/usage` is authorization category C: a staff
   * caller MUST supply it (400 otherwise) and a client-scoped caller MUST NOT
   * (403 otherwise), so the caller's category decides — never a default.
   */
  abstract loadDimensionRows(
    dimension: OpenSetDimension,
    clientCode: string | null,
    window: ResolvedDateRange,
    signal: AbortSignal,
  ): Promise<FilterOptionsFetch<readonly GroupedUsageRow[]>>;

  /**
   * GH-164: the applications, models and providers observed across every client over the probe
   * window. Staff-only (a client-scoped caller receives 403 — hence `ok: false`).
   */
  abstract loadFilterOptions(
    window: ResolvedDateRange,
    signal: AbortSignal,
  ): Promise<FilterOptionsFetch<FilterOptionsResponse>>;

  /** The staff-only client directory. A client-scoped caller receives 403 — hence `ok: false`. */
  abstract loadClientDirectory(
    signal: AbortSignal,
  ): Promise<FilterOptionsFetch<readonly ClientDirectoryEntry[]>>;
}

export class GeneratedFilterOptionsAdapter extends FilterOptionsAdapter {
  private readonly basePath: string;

  constructor(
    apiBaseUrl: string,
    private readonly accessTokenProvider: AccessTokenProvider,
    private readonly fetchApi?: FetchAPI,
  ) {
    super();
    this.basePath = apiBaseUrl.replace(/\/$/, '');
  }

  /**
   * One `Configuration` per request, `GeneratedRealtimeGrantClient`-style: the
   * `accessToken` closure captures *this request's* signal, so aborting the
   * request also aborts a token acquisition still in flight (renewal can block
   * unboundedly — a never-aborted stand-in signal would leave it running).
   */
  private configurationFor(signal: AbortSignal): Configuration {
    return new Configuration({
      basePath: this.basePath,
      fetchApi: this.fetchApi,
      accessToken: () => this.accessTokenProvider.getAccessToken(signal),
    });
  }

  override async loadDimensionRows(
    dimension: OpenSetDimension,
    clientCode: string | null,
    window: ResolvedDateRange,
    signal: AbortSignal,
  ): Promise<FilterOptionsFetch<readonly GroupedUsageRow[]>> {
    try {
      const result = await new UsageApi(this.configurationFor(signal)).getDashboardUsage(
        {
          groupBy: dimension as GetDashboardUsageGroupByEnum,
          fromUtc: new Date(window.fromUtc),
          toUtc: new Date(window.toUtc),
          // Omitted entirely for a client-scoped caller: a supplied value is
          // denied with 403, so caller input never widens the authenticated scope.
          ...(clientCode === null ? {} : { clientCode }),
        },
        { signal },
      );
      return { ok: true, value: result.rows };
    } catch {
      // A 422 row cap, a 400, a 403, a network failure, an unavailable token, a
      // malformed body — the caller needs only "not obtainable" (FR-016).
      return { ok: false };
    }
  }

  override async loadFilterOptions(
    window: ResolvedDateRange,
    signal: AbortSignal,
  ): Promise<FilterOptionsFetch<FilterOptionsResponse>> {
    try {
      const result = await new FilterOptionsApi(this.configurationFor(signal)).getDashboardFilterOptions(
        { fromUtc: new Date(window.fromUtc), toUtc: new Date(window.toUtc) },
        { signal },
      );
      return { ok: true, value: result };
    } catch {
      return { ok: false };
    }
  }

  override async loadClientDirectory(
    signal: AbortSignal,
  ): Promise<FilterOptionsFetch<readonly ClientDirectoryEntry[]>> {
    try {
      const result = await new ClientDirectoryApi(this.configurationFor(signal))
        .getClientDirectory({ signal });
      return { ok: true, value: result.clients };
    } catch {
      return { ok: false };
    }
  }
}
